// src/live/hooks/useMyUpcomingEvents.ts
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { LiveIndexEntry } from '../types/liveIndex';

/**
 * Subscribes to the host's own events (any non-ended status).
 * Returns them as LiveIndexEntry[] so LiveCard can render them directly.
 * Only active when userId is defined.
 */
export function useMyUpcomingEvents(userId: string | undefined): LiveIndexEntry[] {
  const [events, setEvents] = useState<LiveIndexEntry[]>([]);

  useEffect(() => {
    if (!userId) {
      setEvents([]);
      return;
    }

    const q = query(
      collection(db, 'liveEvents'),
      where('hostId', '==', userId),
      where('status', 'in', ['upcoming', 'starting_soon', 'live']),
    );

    const STALE_LIVE_MS = 12 * 60 * 60 * 1000; // 12 hours

    const toMs = (value: unknown): number | null => {
      if (!value) return null;
      if (typeof (value as { toMillis?: () => number }).toMillis === 'function')
        return (value as { toMillis: () => number }).toMillis();
      if (value instanceof Date) return value.getTime();
      return null;
    };

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const entries: LiveIndexEntry[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          source: 'event',
          title: data.title || 'Untitled Event',
          description: data.description || '',
          thumbnailUrl: data.thumbnailUrl || '',
          hostId: data.hostId,
          hostName: data.hostName || 'Host',
          hostAvatar: data.hostAvatar || undefined,
          status: data.status === 'live' ? 'live' : 'upcoming',
          visibility: 'private',
          allowedUsers: data.allowedUsers || [],
          viewers: data.rsvpCounts?.going || 0,
          viewerCount: data.rsvpCounts?.going || 0,
          startedAt: data.startedAt ?? data.scheduledAt ?? new Date(),
          sourceCollection: 'liveEvents',
          category: data.category || undefined,
          tags: data.tags || [],
          _rawStartedAt: data.startedAt,
        } as LiveIndexEntry & { _rawStartedAt: unknown };
      }).filter((entry) => {
        // Guard: a 'live' event older than 12 h was never properly ended.
        // Exclude it so stale test events don't persist on the host's dashboard.
        if (entry.status !== 'live') return true;
        const startMs = toMs((entry as LiveIndexEntry & { _rawStartedAt: unknown })._rawStartedAt);
        if (startMs === null) return true;
        return (Date.now() - startMs) < STALE_LIVE_MS;
      }).map(({ _rawStartedAt: _drop, ...rest }) => rest as LiveIndexEntry);
      setEvents(entries);
    }, () => {
      // Silently ignore errors (e.g. auth not ready yet)
      setEvents([]);
    });

    return () => unsubscribe();
  }, [userId]);

  return events;
}
